export type BlinkSignatureKind =
  | 'arcane_burst'
  | 'flame_wake'
  | 'shadow_step'
  | 'frost_nova'
  | 'seismic_slam'
  | 'gilded_pulse';

export interface BlinkSignatureDef {
  kind: BlinkSignatureKind;
  color: string;
  radius: number;
  baseDamage: number;
  knockback: number;
  burnDps: number;
  burnDur: number;
  slowFactor: number;
  slowDur: number;
  vacuumRadius: number;
  hasteDur: number;
}

export const BLINK_SIGNATURES: Record<string, BlinkSignatureDef> = {
  aria: {
    kind: 'arcane_burst',
    color: '#7ee0ff',
    radius: 4.5,
    baseDamage: 28,
    knockback: 10,
    burnDps: 0,
    burnDur: 0,
    slowFactor: 0,
    slowDur: 0,
    vacuumRadius: 0,
    hasteDur: 0,
  },
  kael: {
    kind: 'flame_wake',
    color: '#ff7832',
    radius: 4.2,
    baseDamage: 22,
    knockback: 6,
    burnDps: 18,
    burnDur: 3,
    slowFactor: 0,
    slowDur: 0,
    vacuumRadius: 0,
    hasteDur: 0,
  },
  nyx: {
    kind: 'shadow_step',
    color: '#c27bff',
    radius: 3.8,
    baseDamage: 20,
    knockback: 4,
    burnDps: 0,
    burnDur: 0,
    slowFactor: 0,
    slowDur: 0,
    vacuumRadius: 0,
    hasteDur: 2.5,
  },
  thora: {
    kind: 'frost_nova',
    color: '#7be3ff',
    radius: 4.6,
    baseDamage: 18,
    knockback: 5,
    burnDps: 0,
    burnDur: 0,
    slowFactor: 0.8,
    slowDur: 2.8,
    vacuumRadius: 0,
    hasteDur: 0,
  },
  brann: {
    kind: 'seismic_slam',
    color: '#f4b942',
    radius: 5.2,
    baseDamage: 38,
    knockback: 22,
    burnDps: 0,
    burnDur: 0,
    slowFactor: 0.45,
    slowDur: 1.8,
    vacuumRadius: 0,
    hasteDur: 0,
  },
  lyra: {
    kind: 'gilded_pulse',
    color: '#ffe26a',
    radius: 4.0,
    baseDamage: 16,
    knockback: 8,
    burnDps: 0,
    burnDur: 0,
    slowFactor: 0,
    slowDur: 0,
    vacuumRadius: 12,
    hasteDur: 0,
  },
};

export interface BlinkHit {
  enemyId: number;
  damage: number;
  dirX: number;
  dirZ: number;
  knockback: number;
  burnDps: number;
  burnDur: number;
  slowFactor: number;
  slowDur: number;
}

export interface BlinkContext {
  x: number;
  z: number;
  might: number;
  area: number;
  masteryRank?: number;
  enemies: ReadonlyArray<{ id: number; x: number; z: number; hp: number }>;
  hitEnemy: (hit: BlinkHit) => void;
  pullGemsRadius: (radius: number) => void;
  grantHasteBurst: (duration: number) => void;
}

export function triggerBlinkSignature(charId: string, ctx: BlinkContext): BlinkSignatureDef {
  const sig = BLINK_SIGNATURES[charId] ?? BLINK_SIGNATURES.aria;
  const ascMult = (ctx.masteryRank ?? 0) >= 3 ? 1.25 : 1;
  const radius = sig.radius * ascMult * (1 + ctx.area * 0.5);
  const dmg = sig.baseDamage * ascMult * (1 + ctx.might);
  const burnDps = sig.burnDps * ascMult * (1 + ctx.might);
  for (const e of ctx.enemies) {
    if (e.hp <= 0) continue;
    const dx = e.x - ctx.x;
    const dz = e.z - ctx.z;
    const d = Math.hypot(dx, dz) || 1;
    if (d <= radius) {
      ctx.hitEnemy({
        enemyId: e.id,
        damage: dmg,
        dirX: dx / d,
        dirZ: dz / d,
        knockback: sig.knockback,
        burnDps,
        burnDur: sig.burnDur,
        slowFactor: sig.slowFactor,
        slowDur: sig.slowDur,
      });
    }
  }
  if (sig.vacuumRadius > 0) {
    ctx.pullGemsRadius(sig.vacuumRadius * (1 + ctx.area * 0.5));
  }
  if (sig.hasteDur > 0) {
    ctx.grantHasteBurst(sig.hasteDur * ascMult);
  }
  return ascMult > 1 ? { ...sig, radius: sig.radius * ascMult, baseDamage: sig.baseDamage * ascMult } : sig;
}
